import { Box, Button, Group, Stack, Text } from "@mantine/core"
import { ArrowsClockwiseIcon, DownloadSimpleIcon, FileTextIcon } from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { DeliveryApi } from "../../apis"
import { message } from "../../utils/message"
import { DeliveryActionButton } from "../delivery/action-controls"
import type { DeliveryActionState } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import classes from "./dn-detail.module.css"
import { PhotoArchivePanel } from "./photo-archive-panel"

type Props = Readonly<{ dnId: string; initialPdf?: string | null; regenerateState: DeliveryActionState }>

/**
 * «Хавсралт» tab: the waybill PDF and the proof of delivery. «Дахин үүсгэх» needs a receipt (BE 404 otherwise)
 * and ORD_DN_PDF; without either it stays visible with the reason (O87, O88).
 */
export function DnAttachments({ dnId, initialPdf, regenerateState }: Props) {
  const { t } = useTranslation("delivery")
  const [pdfUrl, setPdfUrl] = useState<string | null>(initialPdf ?? null)
  const [loading, setLoading] = useState(false)

  const regenerate = async () => {
    setLoading(true)
    try {
      const res = await DeliveryApi.regeneratePdf(dnId)
      if (res?.url) {
        setPdfUrl(res.url)
        window.open(res.url, "_blank", "noopener,noreferrer")
        message.success(t("dnDetail.pdfUpdated"))
      }
    } catch (err) {
      message.error(translateBackendError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Stack gap="lg">
      <Group className={classes.attachRow} justify="space-between" wrap="wrap" gap="md">
        <Group gap="md" wrap="nowrap">
          <Box className={classes.vehicleIcon}>
            <FileTextIcon size={22} />
          </Box>
          <Box>
            <Text className={classes.value}>{t("dnDetail.waybillTitle")}</Text>
            <Text className={classes.label}>{pdfUrl ? t("dnDetail.pdfDoc") : t("dnDetail.notReady")}</Text>
          </Box>
        </Group>
        <Group gap="sm" wrap="nowrap">
          {pdfUrl ? (
            <Button component="a" href={pdfUrl} target="_blank" rel="noopener noreferrer" variant="default" leftSection={<DownloadSimpleIcon size={16} />}>
              {t("dnDetail.download")}
            </Button>
          ) : (
            <DeliveryActionButton state={{ allowed: false, reason: t("dnDetail.notReady") }} variant="default" leftSection={<DownloadSimpleIcon size={16} />}>
              {t("dnDetail.download")}
            </DeliveryActionButton>
          )}
          <DeliveryActionButton
            state={regenerateState}
            variant="default"
            leftSection={<ArrowsClockwiseIcon size={16} />}
            loading={loading}
            onClick={regenerate}
          >
            {t("dnDetail.regenerate")}
          </DeliveryActionButton>
        </Group>
      </Group>
      <PhotoArchivePanel deliveryNoteId={dnId} />
    </Stack>
  )
}
